import React from 'react';
import PropTypes from 'prop-types';
import { useFela } from 'react-fela';
import { Tooltip } from 'react-tooltip';
import { autoPlacement, offset } from '@floating-ui/dom';
import { useI18n } from 'components/I18n';

const OFFSET = 10;
const ALLOWED_PLACEMENTS = ['top', 'top-start', 'top-end'];

const styles = ({ theme }) => ({
  background: theme.color.tooltipBackground,
  color: theme.color.tooltipColor,
  fontSize: theme.fontSize.small,
  fontWeight: 'bold',
  padding: `${theme.margin.fine} ${theme.margin.small}`,
  boxShadow: '0px 2px 8px 2px #00000040',
  zIndex: theme.zIndex.tooltip,
  borderRadius: theme.radius.paper,
  opacity: 0,
});

/**
 * @example
 * <InfoButton data-tooltip-id="main" data-tooltip-i18n="tooltip.moreInfo" />
 * // with i18n-values
 * <InfoButton
 *    data-tooltip-id="main"
 *    data-tooltip-i18n="subscription.price"
 *    data-tooltip-i18n-values={{price: 100}}
 * />
 * // change [data-tooltip-content] if you need hot reload
 * <WatchlistButton
 *    data-tooltip-id="main"
 *    data-tooltip-content={viewable.inMyList ? 'watchlist.remove' : 'watchlist.add'}
 *    data-tooltip-i18n={viewable.inMyList ? 'watchlist.remove' : 'watchlist.add'}
 * />
 */

function LiteTooltip({ position, ...props }) {
  const i18n = useI18n();
  const { css } = useFela();

  return (
    <Tooltip
      closeOnEsc
      noArrow
      opacity="1"
      className={css(styles)}
      positionStrategy="fixed"
      disableStyleInjection
      middlewares={[
        offset(OFFSET),
        autoPlacement({ allowedPlacements: position || ALLOWED_PLACEMENTS }),
      ]}
      {...props}
      render={({ activeAnchor }) => {
        const i18nId = activeAnchor?.getAttribute('data-tooltip-i18n') || '';

        return i18n.hasMessage(i18nId)
          ? i18n.formatText(
            i18nId,
            activeAnchor?.getAttribute('data-tooltip-i18n-values'),
          ) : i18nId;
      }}
    />
  );
}

LiteTooltip.propTypes = {
  position: PropTypes.arrayOf(PropTypes.string),
};

export default LiteTooltip;
